import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { Card, EmptyState, Field, FileList, ProgressBar, Spinner, StatusChip, TypeChip, fmtMoney, inputCls } from "../ui";
import { FilePicker } from "../../components/QuoteSection";

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between gap-6 py-2.5 border-b border-white/5 last:border-0">
      <span className="text-xs tracking-[0.15em] uppercase text-white/40 shrink-0">{label}</span>
      <span className="text-sm text-white/80 text-right">{value}</span>
    </div>
  );
}

function DeliveryCard({ rid, request }) {
  const [data, setData] = useState(null);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ location: "", volume: "", delivery_date: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/admin/requests/${rid}/milestones`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], summary: null }));
  useEffect(() => { load(); }, [rid]);

  const locations = request.locations?.length
    ? request.locations.map((l) => l.name)
    : [request.delivery_location].filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await api.post(`/admin/requests/${rid}/milestones`, fd);
      toast.success("Delivery milestone recorded");
      setForm({ location: "", volume: "", delivery_date: "", notes: "" });
      setFiles([]);
      load();
    } catch (err) {
      toast.error("Failed to record milestone", { description: fmtErr(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Delivery Milestones" testId="admin-delivery-card">
      {data?.summary && (
        <div className="mb-6 space-y-4" data-testid="admin-delivery-progress">
          {data.summary.locations.map((l) => (
            <div key={l.name}>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-white/75">{l.name}</span>
                <span className="text-white/50">{Number(l.delivered).toLocaleString()} / {Number(l.ordered).toLocaleString()} {data.summary.unit}</span>
              </div>
              <ProgressBar delivered={l.delivered} ordered={l.ordered} testId={`admin-progress-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            </div>
          ))}
          <div className="flex justify-between text-sm pt-2 border-t border-white/8">
            <span className="text-white/50">Total delivered</span>
            <span className="text-gold font-outfit" data-testid="admin-delivery-total">
              {Number(data.summary.delivered).toLocaleString()} / {Number(data.summary.ordered).toLocaleString()} {data.summary.unit}
            </span>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="milestone-form">
        {locations.length > 1 ? (
          <Field label="Location">
            <select value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className={inputCls} data-testid="milestone-location-select">
              <option value="" className="bg-surface">Select location</option>
              {locations.map((l) => <option key={l} value={l} className="bg-surface">{l}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Location">
            <input value={form.location || locations[0] || ""} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className={inputCls} data-testid="milestone-location-input" />
          </Field>
        )}
        <Field label="Volume Delivered" required>
          <input required type="number" min="1" value={form.volume} onChange={(e) => setForm((f) => ({ ...f, volume: e.target.value }))} className={inputCls} placeholder={`e.g. 50000 ${request.unit || ""}`} data-testid="milestone-volume-input" />
        </Field>
        <Field label="Delivery Date">
          <input type="date" value={form.delivery_date} onChange={(e) => setForm((f) => ({ ...f, delivery_date: e.target.value }))} className={inputCls} data-testid="milestone-date-input" />
        </Field>
        <Field label="Notes">
          <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} data-testid="milestone-notes-input" />
        </Field>
        <div className="col-span-2">
          <FilePicker files={files} setFiles={setFiles} testId="milestone-file-upload" />
        </div>
        <div className="col-span-2">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-gold text-ink text-sm font-outfit font-medium rounded-full px-7 py-3 hover:bg-goldbright transition-colors disabled:opacity-60" data-testid="milestone-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Record Delivery
          </button>
        </div>
      </form>

      {data && data.items.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/8 space-y-3" data-testid="milestone-list">
          {data.items.map((m) => (
            <div key={m.id} className="border border-white/10 rounded-lg p-4">
              <div className="flex justify-between text-sm">
                <span className="text-white/80">{Number(m.volume).toLocaleString()} {data.summary?.unit}{m.location ? ` — ${m.location}` : ""}</span>
                <span className="text-white/40">{m.delivery_date}</span>
              </div>
              {m.notes && <p className="text-xs text-white/45 mt-1">{m.notes}</p>}
              <p className="text-[11px] text-white/30 mt-1">Recorded by {m.recorded_by}</p>
              {m.files?.length > 0 && <div className="mt-2"><FileList files={m.files} /></div>}
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-white/30 mt-4">Completion stays manual — mark the request DELIVERED/COMPLETED via Status when appropriate.</p>
    </Card>
  );
}

function AiAssistantCard({ rid, hasOffers }) {
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(null);

  const run = async (kind) => {
    if (busy) return;
    setBusy(kind);
    setOutput(null);
    try {
      const res = await api.post(`/admin/requests/${rid}/ai/${kind}`);
      setOutput({ kind, text: res.data.text });
    } catch (err) {
      toast.error("AI assistant failed", { description: fmtErr(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title="AI Procurement Assistant" testId="ai-assistant-card" className="lg:col-span-2">
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => run("draft-rfq")}
          disabled={!!busy}
          className="inline-flex items-center gap-2 border border-gold/50 text-gold text-sm font-outfit rounded-full px-6 py-2.5 hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
          data-testid="ai-draft-rfq-btn"
        >
          {busy === "draft-rfq" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Draft RFQ for Suppliers
        </button>
        <button
          onClick={() => run("summarize-offers")}
          disabled={!!busy || !hasOffers}
          className="inline-flex items-center gap-2 border border-volt/50 text-volt text-sm font-outfit rounded-full px-6 py-2.5 hover:bg-volt hover:text-ink transition-colors disabled:opacity-50"
          data-testid="ai-summarize-offers-btn"
        >
          {busy === "summarize-offers" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Summarize Supplier Offers
        </button>
      </div>
      {busy && <p className="text-xs text-white/40 mt-4">Ajemi AI is working…</p>}
      {output && (
        <div className="mt-5 border border-white/10 rounded-xl p-5" data-testid="ai-output">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/40">
              {output.kind === "draft-rfq" ? "Draft RFQ" : "Offer Summary"}
            </span>
            <button
              onClick={() => { navigator.clipboard.writeText(output.text); toast.success("Copied"); }}
              className="text-xs text-gold hover:text-goldbright transition-colors"
              data-testid="ai-copy-btn"
            >
              Copy
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-white/75 font-sans leading-relaxed">{output.text}</pre>
        </div>
      )}
      <p className="text-[11px] text-white/30 mt-4">
        AI-generated draft for review only. Ajemi staff verify everything and make all final
        commercial decisions.
      </p>
    </Card>
  );
}

export default function AdminRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [econ, setEcon] = useState({ model: "margin", supplier_price: "", ajemi_margin: "", customer_price: "", volume: "" });
  const [prop, setProp] = useState({ price_per_unit: "", quantity: "", delivery_terms: "", payment_terms: "", delivery_timeline: "", validity: "", notes: "" });
  const [cycleLabel, setCycleLabel] = useState("");
  const [cycleDue, setCycleDue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/admin/requests/${id}`).then((r) => {
      setD(r.data);
      if (r.data.commercial) {
        setEcon({
          model: r.data.commercial.model, supplier_price: r.data.commercial.supplier_price,
          ajemi_margin: r.data.commercial.ajemi_margin, customer_price: r.data.commercial.customer_price,
          volume: r.data.commercial.volume,
        });
      }
    }).catch(() => setD(false));

  useEffect(() => { load(); }, [id]);

  if (d === null) return <div className="py-24"><Spinner /></div>;
  if (d === false) return <EmptyState title="Request not found" text="This procurement request does not exist." />;

  const r = d.request;
  const ref = r.request_ref || r.reference_id;

  const run = async (fn, successMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      load();
    } catch (err) {
      toast.error("Action failed", { description: fmtErr(err) });
    } finally {
      setBusy(false);
    }
  };

  const pickOffer = (offer) => {
    setSelectedOffer(offer);
    setProp((p) => ({
      ...p,
      price_per_unit: econ.customer_price || offer.price_per_unit,
      quantity: offer.quantity || r.quantity,
      delivery_terms: p.delivery_terms || offer.conditions || "",
      payment_terms: offer.payment_terms || "",
      delivery_timeline: offer.delivery_timeline || "",
      validity: offer.validity || "",
    }));
    setEcon((e) => ({ ...e, supplier_price: e.supplier_price || offer.price_per_unit, volume: e.volume || r.quantity }));
  };

  const estMargin = econ.model === "fee"
    ? (Number(econ.ajemi_margin) || 0) * (Number(econ.volume) || 0)
    : ((Number(econ.customer_price) || 0) - (Number(econ.supplier_price) || 0)) * (Number(econ.volume) || 0);

  return (
    <div data-testid="admin-request-detail" className="max-w-5xl">
      <button onClick={() => navigate("/admin")} className="text-sm text-white/45 hover:text-gold transition-colors mb-6" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4 inline mr-1" /> Procurement Control
      </button>

      <div className="flex flex-wrap items-center gap-4 mb-8">
        <h1 className="font-outfit text-3xl font-extralight tracking-tight text-white" data-testid="admin-request-ref">{ref}</h1>
        <TypeChip type={r.request_type || r.type} />
        <StatusChip status={r.status === "new" ? "SUBMITTED" : r.status} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Customer" testId="admin-customer-card">
          <Row label="Company" value={r.company_name} />
          <Row label="Contact" value={r.contact_name} />
          <Row label="Email" value={r.email} />
          <Row label="Phone" value={r.phone} />
          <Row label="Account" value={d.customer ? d.customer.email : "Website submission (no account)"} />
        </Card>

        <Card title="Requirement" testId="admin-requirement-card">
          <Row label="Product" value={r.product} />
          <Row label="Quantity" value={`${r.quantity} ${r.unit || ""}`} />
          <Row label="Location" value={r.delivery_location} />
          <Row label="Required" value={r.required_date} />
          <Row label="Start" value={r.start_date} />
          <Row label="Frequency" value={r.frequency} />
          <Row label="Duration" value={r.expected_duration} />
          <Row label="Specs" value={r.specs} />
          <Row label="Notes" value={r.notes} />
          {r.locations?.length > 0 && (
            <div className="pt-4">
              <p className="text-xs tracking-[0.15em] uppercase text-white/40 mb-2">Locations</p>
              {r.locations.map((l, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5">
                  <span className="text-white/75">{l.name}</span>
                  <span className="text-white/50">{l.volume} {r.unit}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Documents" testId="admin-documents-card">
          <FileList files={r.files} />
        </Card>

        <DeliveryCard rid={id} request={r} />

        <Card title="Status" testId="admin-status-card"
          action={<StatusChip status={r.status === "new" ? "SUBMITTED" : r.status} />}>
          <div className="flex gap-3">
            <select id="admin-status-select" className={inputCls} defaultValue={r.status === "new" ? "SUBMITTED" : r.status} data-testid="admin-status-select">
              {d.statuses.map((s) => <option key={s} value={s} className="bg-surface">{s}</option>)}
            </select>
            <button
              onClick={() => run(() => api.post(`/admin/requests/${id}/status`, { status: document.getElementById("admin-status-select").value }), "Status updated")}
              disabled={busy}
              className="shrink-0 bg-gold text-ink text-sm font-outfit font-medium rounded-full px-6 hover:bg-goldbright transition-colors disabled:opacity-60"
              data-testid="admin-status-update-btn"
            >
              Update
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {[...(r.status_history || [])].reverse().slice(0, 6).map((h, i) => (
              <p key={i} className="text-xs text-white/40">{h.status} — {new Date(h.at).toLocaleString()} · {h.by}</p>
            ))}
          </div>
        </Card>

        <Card title="Supplier Matching" testId="admin-matching-card">
          {d.suppliers.length === 0 ? (
            <p className="text-sm text-white/40">No active suppliers. Approve suppliers from the Suppliers tab first.</p>
          ) : (
            <>
              <div className="space-y-2 mb-5 max-h-44 overflow-y-auto" data-lenis-prevent>
                {d.suppliers.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#D9A441] w-4 h-4"
                      checked={selectedSuppliers.includes(s.id)}
                      onChange={(e) => setSelectedSuppliers((xs) => e.target.checked ? [...xs, s.id] : xs.filter((x) => x !== s.id))}
                      data-testid={`invite-checkbox-${s.id}`}
                    />
                    {s.company_name}
                    <span className="text-xs text-white/35">{s.profile?.products_supplied}</span>
                  </label>
                ))}
              </div>
              <button
                onClick={() => run(() => api.post(`/admin/requests/${id}/invite`, { supplier_ids: selectedSuppliers }).then((res) => toast.success(`${res.data.invited} supplier(s) invited`)), null)}
                disabled={busy || selectedSuppliers.length === 0}
                className="border border-gold/50 text-gold text-sm font-outfit rounded-full px-6 py-2.5 hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
                data-testid="admin-invite-btn"
              >
                Send RFQ to Selected Suppliers
              </button>
            </>
          )}
          {d.opportunities.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/8 space-y-2">
              {d.opportunities.map((o) => (
                <div key={o.id} className="flex justify-between text-sm">
                  <span className="text-white/70">{o.supplier_company}</span>
                  <span className="text-xs text-white/40 capitalize">{o.status.replace("_", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <AiAssistantCard rid={id} hasOffers={d.offers.length > 0} />

        <Card title={`Supplier Offers (${d.offers.length})`} testId="admin-offers-card">          {d.offers.length === 0 ? (
            <p className="text-sm text-white/40">No offers yet. Invite suppliers to receive offers.</p>
          ) : (
            <div className="space-y-4">
              {d.offers.map((o) => (
                <div key={o.id} className={`border rounded-xl p-4 ${selectedOffer?.id === o.id ? "border-gold/60 bg-gold/5" : "border-white/10"}`} data-testid={`admin-offer-${o.id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <p className="text-white font-outfit">{o.supplier_company}</p>
                      <p className="text-sm text-white/60 mt-1">{fmtMoney(o.price_per_unit)} / unit · {Number(o.quantity || 0).toLocaleString()} units</p>
                      <p className="text-xs text-white/40 mt-1">{o.delivery_timeline} · {o.payment_terms} · valid {o.validity}</p>
                      {o.spec_confirmed && <p className="text-xs text-emerald-300 mt-1">Spec confirmed</p>}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${o.status === "selected" ? "border-emerald-400/50 text-emerald-300" : o.status === "rejected" ? "border-white/15 text-white/40" : "border-gold/50 text-gold"}`}>{o.status}</span>
                      <button onClick={() => pickOffer(o)} className="text-xs text-gold hover:text-goldbright transition-colors" data-testid={`select-offer-${o.id}`}>
                        Select &amp; build proposal →
                      </button>
                    </div>
                  </div>
                  {o.files?.length > 0 && <div className="mt-3"><FileList files={o.files} /></div>}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Commercial Analysis (internal)" testId="admin-economics-card">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Model">
              <select value={econ.model} onChange={(e) => setEcon((x) => ({ ...x, model: e.target.value }))} className={inputCls} data-testid="econ-model-select">
                <option value="margin" className="bg-surface">Procurement margin</option>
                <option value="fee" className="bg-surface">Service fee</option>
              </select>
            </Field>
            <Field label="Volume">
              <input type="number" value={econ.volume} onChange={(e) => setEcon((x) => ({ ...x, volume: e.target.value }))} className={inputCls} data-testid="econ-volume-input" />
            </Field>
            <Field label="Supplier Price / unit">
              <input type="number" step="0.01" value={econ.supplier_price} onChange={(e) => setEcon((x) => ({ ...x, supplier_price: e.target.value }))} className={inputCls} data-testid="econ-supplier-price-input" />
            </Field>
            <Field label={econ.model === "fee" ? "Ajemi Fee / unit" : "Ajemi Margin / unit"}>
              <input type="number" step="0.01" value={econ.ajemi_margin} onChange={(e) => setEcon((x) => ({ ...x, ajemi_margin: e.target.value }))} className={inputCls} data-testid="econ-margin-input" />
            </Field>
            <Field label="Customer Price / unit">
              <input type="number" step="0.01" value={econ.customer_price} onChange={(e) => setEcon((x) => ({ ...x, customer_price: e.target.value }))} className={inputCls} data-testid="econ-customer-price-input" />
            </Field>
            <div className="flex flex-col justify-end">
              <p className="text-xs text-white/40 uppercase tracking-wider">Est. gross margin</p>
              <p className="font-outfit text-2xl text-emerald-300 mt-1" data-testid="econ-est-margin">{fmtMoney(estMargin)}</p>
            </div>
          </div>
          <button
            onClick={() => run(() => api.post(`/admin/requests/${id}/economics`, { ...econ, supplier_price: Number(econ.supplier_price) || 0, ajemi_margin: Number(econ.ajemi_margin) || 0, customer_price: Number(econ.customer_price) || 0, volume: Number(econ.volume) || 0 }), "Economics saved")}
            disabled={busy}
            className="mt-5 border border-white/20 text-white/80 text-sm font-outfit rounded-full px-6 py-2.5 hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-50"
            data-testid="econ-save-btn"
          >
            Save Internal Economics
          </button>
          <p className="text-[11px] text-white/30 mt-3">Internal only — never visible to customers or suppliers.</p>
        </Card>

        <Card title="Customer Proposal" testId="admin-proposal-card">
          {d.proposal && (
            <div className="mb-5 border border-white/10 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-white/70">Current proposal — {fmtMoney(d.proposal.price_per_unit, d.proposal.currency)}/unit · total {fmtMoney(d.proposal.total_price, d.proposal.currency)}</p>
                <StatusChip status={d.proposal.status.toUpperCase()} />
              </div>
              {d.proposal.customer_note && <p className="text-xs text-gold mt-2">Customer: {d.proposal.customer_note}</p>}
            </div>
          )}
          {selectedOffer && <p className="text-xs text-gold mb-4" data-testid="proposal-from-offer">Building from {selectedOffer.supplier_company}'s offer</p>}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price / unit" required>
              <input type="number" step="0.01" value={prop.price_per_unit} onChange={(e) => setProp((p) => ({ ...p, price_per_unit: e.target.value }))} className={inputCls} data-testid="prop-price-input" />
            </Field>
            <Field label="Quantity">
              <input type="number" value={prop.quantity} onChange={(e) => setProp((p) => ({ ...p, quantity: e.target.value }))} className={inputCls} placeholder={r.quantity} data-testid="prop-quantity-input" />
            </Field>
            <Field label="Delivery Terms">
              <input value={prop.delivery_terms} onChange={(e) => setProp((p) => ({ ...p, delivery_terms: e.target.value }))} className={inputCls} data-testid="prop-delivery-terms-input" />
            </Field>
            <Field label="Payment Terms">
              <input value={prop.payment_terms} onChange={(e) => setProp((p) => ({ ...p, payment_terms: e.target.value }))} className={inputCls} data-testid="prop-payment-terms-input" />
            </Field>
            <Field label="Delivery Timeline">
              <input value={prop.delivery_timeline} onChange={(e) => setProp((p) => ({ ...p, delivery_timeline: e.target.value }))} className={inputCls} data-testid="prop-timeline-input" />
            </Field>
            <Field label="Quote Validity">
              <input value={prop.validity} onChange={(e) => setProp((p) => ({ ...p, validity: e.target.value }))} className={inputCls} data-testid="prop-validity-input" />
            </Field>
            <div className="col-span-2">
              <Field label="Notes for Customer">
                <input value={prop.notes} onChange={(e) => setProp((p) => ({ ...p, notes: e.target.value }))} className={inputCls} data-testid="prop-notes-input" />
              </Field>
            </div>
          </div>
          <button
            onClick={() => run(() => api.post(`/admin/requests/${id}/proposal`, {
              offer_id: selectedOffer?.id || null,
              price_per_unit: Number(prop.price_per_unit),
              quantity: Number(prop.quantity) || 0,
              unit: r.unit || "Litres",
              delivery_terms: prop.delivery_terms, payment_terms: prop.payment_terms,
              delivery_timeline: prop.delivery_timeline, validity: prop.validity, notes: prop.notes,
            }), "Proposal sent to customer")}
            disabled={busy || !prop.price_per_unit}
            className="mt-5 inline-flex items-center gap-2 bg-gold text-ink text-sm font-outfit font-medium rounded-full px-7 py-3 hover:bg-goldbright transition-colors disabled:opacity-50"
            data-testid="prop-send-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Send Proposal to Customer
          </button>
        </Card>

        <Card title="Recurring Supply Cycles" testId="admin-cycles-card">
          <div className="flex gap-3 mb-5">
            <input value={cycleLabel} onChange={(e) => setCycleLabel(e.target.value)} className={inputCls} placeholder="e.g. September 2026 cycle" data-testid="cycle-label-input" />
            <input type="date" value={cycleDue} onChange={(e) => setCycleDue(e.target.value)} className={`${inputCls} max-w-[170px]`} data-testid="cycle-due-input" />
            <button
              onClick={() => run(() => api.post(`/admin/requests/${id}/cycles`, { label: cycleLabel, due_date: cycleDue }).then(() => { setCycleLabel(""); setCycleDue(""); }), "Cycle added")}
              disabled={busy || !cycleLabel}
              className="shrink-0 border border-gold/50 text-gold text-sm font-outfit rounded-full px-6 hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
              data-testid="cycle-add-btn"
            >
              Add
            </button>
          </div>
          {d.cycles.length === 0 ? (
            <p className="text-sm text-white/40">No cycles yet. Add monthly cycles to manage recurring programs.</p>
          ) : (
            <div className="space-y-2">
              {d.cycles.map((c) => (
                <div key={c.id} className="flex justify-between items-center gap-3 text-sm">
                  <span className="text-white/75">{c.label}{c.due_date ? <span className="text-white/35 text-xs"> · due {c.due_date}</span> : null}</span>
                  <select
                    value={c.status}
                    onChange={(e) => run(() => api.post(`/admin/cycles/${c.id}/status`, { status: e.target.value }), null)}
                    className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
                    data-testid={`cycle-status-${c.id}`}
                  >
                    {["planned", "sourcing", "fulfillment", "delivered", "completed"].map((s) => <option key={s} value={s} className="bg-surface">{s}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
